import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Save, Plus, Trash2, CreditCard, RefreshCw, X, ArrowUp, ArrowDown, Eye, EyeOff, Landmark, Bitcoin } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-[13.5px] outline-none focus:border-neutral-400';

const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 32);
const emptyMethod = { id: '', kind: 'crypto', label: '', network: '', color: '#1e293b', icon: '', address: '', coins: '', providers: '', active: true, beneficiary: {} };

const KEY_MAP = { label: 'label', name: 'label', title: 'label', network: 'network', chain: 'network', address: 'address', wallet: 'address', 'wallet address': 'address', coins: 'coins', coin: 'coins', currency: 'coins', providers: 'providers', provider: 'providers', icon: 'icon', color: 'color', colour: 'color' };

const parseBulk = (text, kind) => {
  const out = { beneficiary: {} };
  text.split(/\r?\n/).map(l => l.trim()).filter(Boolean).forEach(line => {
    const m = line.match(/^([^:=\t]{1,40})\s*[:=\t]\s*(.+)$/);
    if (!m) { if (kind === 'crypto' && !out.address && /^[A-Za-z0-9]{20,}$/.test(line)) out.address = line; return; }
    const key = m[1].trim(), val = m[2].trim();
    const f = KEY_MAP[key.toLowerCase()];
    if (f && !(kind === 'bank' && f === 'address')) out[f] = val;
    else if (kind === 'bank') out.beneficiary[key] = val;
  });
  return out;
};

const BulkPaste = ({ kind, onApply }) => {
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const apply = () => {
    const r = parseBulk(text, kind);
    const n = Object.keys(r).filter(k => k !== 'beneficiary').length + Object.keys(r.beneficiary).length;
    onApply(r); setNote(n ? `Filled ${n} field${n > 1 ? 's' : ''}` : 'Nothing recognised — use "Field: value" on each line'); if (n) setText('');
  };
  return (
    <div data-testid="payment-bulk-paste" className="rounded-lg border border-dashed border-emerald-300 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 p-3 space-y-2">
      <div className="text-[11px] tracking-widest uppercase text-emerald-700 dark:text-emerald-400 font-semibold">Paste all details in one go</div>
      <textarea data-testid="payment-bulk-input" rows={4} className={inputCls + ' font-mono text-[12.5px]'} value={text} onChange={(e) => setText(e.target.value)}
        placeholder={kind === 'bank' ? 'Account Name: Healsglobe Exports\nAccount Number: 123456789\nIFSC: ABCD0123456\nProviders: Wise, Remitly' : 'Label: USDT (TRC20)\nNetwork: Tron (TRC20)\nAddress: TXyz...\nCoins: USDT'} />
      <div className="flex items-center gap-3">
        <button type="button" data-testid="payment-bulk-apply" onClick={apply} disabled={!text.trim()} className="btn-brand rounded-md px-3 py-1.5 text-[12.5px] font-semibold disabled:opacity-50">Fill form</button>
        {note && <span data-testid="payment-bulk-note" className="text-[12px] text-neutral-500">{note}</span>}
      </div>
    </div>
  );
};

const PaymentsPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {isNew, ...form}
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setMsg('');
    try {
      const { data } = await axios.get(`${API}/admin/payment-methods`, { headers });
      setMethods(data);
    } catch (e) { setMsg('Failed to load: ' + (e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ ...emptyMethod, isNew: true });
  const startEdit = (m) => setEditing({
    ...m,
    isNew: false,
    coins: (m.coins || []).join(', '),
    providers: (m.providers || []).join(', '),
    beneficiary: m.beneficiary || {},
    _origId: m.id,
  });
  const cancel = () => setEditing(null);
  const upd = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));
  const updBen = (k, v) => setEditing(prev => ({ ...prev, beneficiary: { ...(prev.beneficiary || {}), [k]: v } }));
  const rmBenKey = (k) => setEditing(prev => {
    const b = { ...(prev.beneficiary || {}) };
    delete b[k];
    return { ...prev, beneficiary: b };
  });
  const addBenKey = () => {
    const label = window.prompt('Field name (e.g. Swift Code)');
    if (label && label.trim()) updBen(label.trim(), '');
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true); setMsg('');
    try {
      const payload = {
        kind: editing.kind,
        label: editing.label.trim(),
        network: editing.network || '',
        color: editing.color || '#1e293b',
        icon: editing.icon || '',
        address: editing.address || '',
        coins: (editing.coins || '').split(',').map(s => s.trim()).filter(Boolean),
        providers: (editing.providers || '').split(',').map(s => s.trim()).filter(Boolean),
        beneficiary: editing.beneficiary || {},
        active: !!editing.active,
      };
      if (editing.isNew) {
        payload.id = editing.id.trim().toLowerCase();
        if (!payload.id) throw new Error('ID required');
        await axios.post(`${API}/admin/payment-methods`, payload, { headers });
      } else {
        await axios.patch(`${API}/admin/payment-methods/${editing._origId}`, payload, { headers });
      }
      setEditing(null);
      await load();
    } catch (e) { setMsg('Save failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete payment method "${m.label}"?`)) return;
    try { await axios.delete(`${API}/admin/payment-methods/${m.id}`, { headers }); await load(); }
    catch (e) { setMsg('Delete failed: ' + (e?.response?.data?.detail || e.message)); }
  };

  const toggleActive = async (m) => {
    try { await axios.patch(`${API}/admin/payment-methods/${m.id}`, { active: !m.active }, { headers }); await load(); }
    catch (e) { setMsg('Toggle failed'); }
  };

  const move = async (m, direction) => {
    const idx = methods.findIndex(x => x.id === m.id);
    const other = methods[direction === 'up' ? idx - 1 : idx + 1];
    if (!other) return;
    try {
      await Promise.all([
        axios.patch(`${API}/admin/payment-methods/${m.id}`, { sort_order: other.sort_order }, { headers }),
        axios.patch(`${API}/admin/payment-methods/${other.id}`, { sort_order: m.sort_order }, { headers }),
      ]);
      await load();
    } catch { setMsg('Reorder failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><CreditCard size={20} className="text-neutral-400" /> Payment methods</h2>
          <p className="text-[12.5px] text-neutral-500">Crypto wallets and bank details shown to customers at checkout.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px]"><RefreshCw size={13} /> Refresh</button>
          <button data-testid="add-payment-btn" onClick={startNew} className="inline-flex items-center gap-1.5 bg-[#1e293b] hover:bg-[#0f172a] text-white rounded-md px-4 py-2 text-[13.5px]"><Plus size={14} /> Add method</button>
        </div>
      </div>

      {msg && <div className="text-red-600 text-[13px]">{msg}</div>}

      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[10.5px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200 dark:border-neutral-800">
          <div className="col-span-2">Order / Kind</div>
          <div className="col-span-3">Label / ID</div>
          <div className="col-span-4">Address / Bank</div>
          <div className="col-span-1 text-center">Active</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? <div className="p-6 text-center text-neutral-500 text-[13.5px]">Loading…</div> :
          methods.map((m) => (
            <div key={m.id} data-testid={`payment-row-${m.id}`} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
              <div className="col-span-2 flex items-center gap-1">
                <span className="text-[12px] text-neutral-500 w-5">{m.sort_order}</span>
                <button onClick={() => move(m, 'up')} className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500"><ArrowUp size={12} /></button>
                <button onClick={() => move(m, 'down')} className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500"><ArrowDown size={12} /></button>
                {m.kind === 'bank'
                  ? <Landmark size={13} className="text-neutral-500 ml-1" />
                  : <Bitcoin size={13} className="text-neutral-500 ml-1" />}
              </div>
              <div className="col-span-3">
                <div className="text-[13.5px] text-neutral-900 dark:text-neutral-100 truncate">{m.label}</div>
                <div className="text-[11px] font-mono text-neutral-500 truncate">{m.id} · {m.network}</div>
              </div>
              <div className="col-span-4 text-[11.5px] font-mono text-neutral-500 truncate">{m.kind === 'crypto' ? m.address : Object.entries(m.beneficiary || {}).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>
              <div className="col-span-1 flex justify-center">
                <button onClick={() => toggleActive(m)} data-testid={`payment-toggle-${m.id}`} title={m.active ? 'Active — click to hide' : 'Hidden — click to show'} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${m.active ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}`}>
                  {m.active ? <Eye size={11} /> : <EyeOff size={11} />} {m.active ? 'Live' : 'Hidden'}
                </button>
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <button data-testid={`payment-edit-${m.id}`} onClick={() => startEdit(m)} className="px-2 py-1 text-[12px] rounded-md border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800">Edit</button>
                <button data-testid={`payment-delete-${m.id}`} onClick={() => remove(m)} className="p-1.5 rounded-md text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-red-500"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={cancel}>
          <div data-testid="payment-form-modal" className="mt-10 w-full max-w-2xl bg-white dark:bg-[#141416] rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
              <h3 className="font-serif text-[18px] text-neutral-900 dark:text-neutral-100">{editing.isNew ? 'Add payment method' : 'Edit payment method'}</h3>
              <button onClick={cancel} className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <BulkPaste kind={editing.kind} onApply={({ beneficiary, ...rest }) => setEditing(prev => ({
                ...prev, ...rest,
                ...(prev.isNew && rest.label && !prev.id ? { id: slugify(rest.label) } : {}),
                beneficiary: { ...(prev.beneficiary || {}), ...beneficiary },
              }))} />
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">ID (slug)</span>
                  <input data-testid="payment-id-input" className={inputCls + (editing.isNew ? '' : ' opacity-60 cursor-not-allowed')} disabled={!editing.isNew} value={editing.id} onChange={(e) => upd('id', slugify(e.target.value))} placeholder="e.g. dogecoin" />
                </label>
                <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Kind</span>
                  <select data-testid="payment-kind-select" className={inputCls} value={editing.kind} onChange={(e) => upd('kind', e.target.value)}>
                    <option value="crypto">Crypto wallet</option>
                    <option value="bank">Bank / Remittance</option>
                  </select>
                </label>
                <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Label (customer-facing)</span>
                  <input data-testid="payment-label-input" className={inputCls} value={editing.label} onChange={(e) => upd('label', e.target.value)} placeholder="e.g. Dogecoin" />
                </label>
                <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Network</span>
                  <input className={inputCls} value={editing.network} onChange={(e) => upd('network', e.target.value)} placeholder="e.g. Dogecoin (DOGE) / Wise / Remitly" />
                </label>
                <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Icon (single char)</span>
                  <input className={inputCls} maxLength={2} value={editing.icon} onChange={(e) => upd('icon', e.target.value)} placeholder="e.g. Ð" />
                </label>
                <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Color (hex)</span>
                  <input className={inputCls} value={editing.color} onChange={(e) => upd('color', e.target.value)} placeholder="#F7931A" />
                </label>
              </div>

              {editing.kind === 'crypto' ? (
                <div className="space-y-3">
                  <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Wallet address</span>
                    <input data-testid="payment-address-input" className={inputCls + ' font-mono text-[12.5px]'} value={editing.address} onChange={(e) => upd('address', e.target.value)} />
                  </label>
                  <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Coins accepted (comma-separated)</span>
                    <input className={inputCls} value={editing.coins} onChange={(e) => upd('coins', e.target.value)} placeholder="BTC, LN-BTC" />
                  </label>
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Providers (comma-separated)</span>
                    <input className={inputCls} value={editing.providers} onChange={(e) => upd('providers', e.target.value)} placeholder="Wise, Remitly, Xoom" />
                  </label>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] tracking-widest uppercase text-neutral-500">Bank / beneficiary details</span>
                      <button type="button" onClick={addBenKey} className="text-[12px] border border-neutral-200 dark:border-neutral-700 rounded-md px-2 py-1 inline-flex items-center gap-1"><Plus size={11} /> Add field</button>
                    </div>
                    <div className="space-y-1.5">
                      {Object.entries(editing.beneficiary || {}).map(([k, v]) => (
                        <div key={k} className="grid grid-cols-12 gap-2">
                          <div className="col-span-4 text-[12px] text-neutral-600 dark:text-neutral-300 truncate self-center">{k}</div>
                          <input className={`col-span-7 ${inputCls}`} value={v} onChange={(e) => updBen(k, e.target.value)} />
                          <button onClick={() => rmBenKey(k)} className="col-span-1 rounded-md text-neutral-500 hover:text-red-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center justify-center"><Trash2 size={12} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <label className="inline-flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => upd('active', e.target.checked)} className="w-4 h-4 accent-neutral-800" />
                Active (show at checkout)
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 dark:border-neutral-800">
              <button onClick={cancel} className="px-4 py-2 rounded-md border border-neutral-200 dark:border-neutral-700 text-[13.5px]">Cancel</button>
              <button data-testid="payment-save-btn" onClick={save} disabled={saving || !editing.label.trim() || (editing.isNew && !editing.id.trim())} className="inline-flex items-center gap-1.5 bg-[#1e293b] hover:bg-[#0f172a] text-white rounded-md px-4 py-2 text-[13.5px] disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsPanel;
